import { useEffect } from 'react';
import type { RefObject } from 'react';

import { getLogger } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';

type ChartInstance = {
  invalidateSize?: () => void;
  svgContainer?: { measure?: () => void };
};

const log = getLogger('AIChat.useAmChartsResize');

const logDebug = (message: string, details?: Record<string, unknown>): void => {
  if (isDevMode()) {
    log.debug(message, details);
  }
};

const resizeChart = (chartInstance: ChartInstance | null): void => {
  if (!chartInstance) {
    logDebug('resizeChart: no chart instance');
    return;
  }
  if (typeof chartInstance.invalidateSize === 'function') {
    logDebug('resizeChart: invalidateSize');
    chartInstance.invalidateSize();
    return;
  }
  if (chartInstance.svgContainer && typeof chartInstance.svgContainer.measure === 'function') {
    logDebug('resizeChart: svgContainer.measure');
    chartInstance.svgContainer.measure();
    return;
  }
  logDebug('resizeChart: no resize method');
};

const useAmChartsResize = (
  containerRef: RefObject<HTMLElement | null>,
  chartRef: RefObject<ChartInstance | null>,
): void => {
  useEffect(() => {
    const element = containerRef.current;
    if (!element || !(element instanceof Element)) {
      logDebug('useAmChartsResize: invalid container');
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      logDebug('useAmChartsResize: ResizeObserver missing');
      resizeChart(chartRef.current);
      return;
    }

    let frameId = 0;
    let initialFrameId = 0;
    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      logDebug('useAmChartsResize: resize detected', {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      });
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      if (typeof requestAnimationFrame === 'function') {
        frameId = requestAnimationFrame(() => resizeChart(chartRef.current));
      } else {
        resizeChart(chartRef.current);
      }
    });

    observer.observe(element);

    if (typeof requestAnimationFrame === 'function') {
      initialFrameId = requestAnimationFrame(() => resizeChart(chartRef.current));
    } else {
      resizeChart(chartRef.current);
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      if (initialFrameId) {
        cancelAnimationFrame(initialFrameId);
      }
      observer.disconnect();
    };
  }, [chartRef, containerRef]);
};

export default useAmChartsResize;
